import { useCallback, useEffect, useState } from "react";
import { FileSpreadsheet, Printer, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, downloadAgenda, pad, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { FieldSelect } from "@/components/FieldSelect";
import { Button } from "@/components/ui/button";

export const BULAN_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const now = new Date();
const YEARS = Array.from({ length: 5 }, (_, i) => String(now.getFullYear() - i));

export default function AgendaSurat() {
  const [tahun, setTahun] = useState(String(now.getFullYear()));
  const [bulan, setBulan] = useState(String(now.getMonth() + 1));
  const [rows, setRows] = useState([]);

  const load = useCallback(() => {
    api.get("/agenda", { params: { tahun, ...(bulan && { bulan }) } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [tahun, bulan]);
  useEffect(() => {
    load();
  }, [load]);

  const periode = bulan ? `${BULAN_ID[bulan - 1]} ${tahun}` : `Tahun ${tahun}`;
  const printUrl = `/cetak-rekap-surat?tahun=${tahun}${bulan ? `&bulan=${bulan}` : ""}`;

  return (
    <div data-testid="agenda-page">
      <PageHeader kicker="Arsip Surat Keluar" title="Buku Agenda Surat">
        <FieldSelect value={bulan} onChange={setBulan} options={BULAN_ID.map((b, i) => ({ value: String(i + 1), label: b }))} allLabel="Semua bulan" testid="agenda-bulan" className="w-40" />
        <FieldSelect value={tahun} onChange={(v) => setTahun(v || String(now.getFullYear()))} options={YEARS} testid="agenda-tahun" className="w-28" />
        <Button variant="outline" onClick={() => downloadAgenda(tahun, bulan).catch((e) => toast.error(errMsg(e)))} data-testid="agenda-export-button">
          <FileSpreadsheet className="h-4 w-4 mr-2" /> Ekspor Excel
        </Button>
        <Button asChild className="bg-[#0b4f4a] hover:bg-[#072f2c]" data-testid="agenda-print-button">
          <a href={printUrl} target="_blank" rel="noreferrer"><Printer className="h-4 w-4 mr-2" /> Cetak Rekap Surat</a>
        </Button>
      </PageHeader>

      <div className="flex items-center gap-3 mb-4">
        <span className="h-10 w-10 rounded-xl bg-rose-50 text-rose-600 grid place-items-center"><BookOpen className="h-5 w-5" /></span>
        <div>
          <div className="font-semibold text-slate-900" data-testid="agenda-periode">Periode {periode}</div>
          <div className="text-xs text-slate-500" data-testid="agenda-count">{rows.length} surat keluar tercatat</div>
        </div>
      </div>

      <div className="bg-white border rounded-2xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="agenda-table">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
            <tr>{["No", "Tanggal", "Nomor Surat", "Jenis / Perihal", "Nama Pemohon", "NIK", "RT", "Dicatat oleh"].map((h) => <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} className="border-t hover:bg-slate-50/80" data-testid={`agenda-row-${r.id}`}>
                <td className="px-4 py-3 text-slate-500">{i + 1}</td>
                <td className="px-4 py-3 whitespace-nowrap">{fmtDate(r.tanggal)}</td>
                <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{r.nomor}</td>
                <td className="px-4 py-3">{r.judul}</td>
                <td className="px-4 py-3 font-semibold whitespace-nowrap">{r.nama}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.nik}</td>
                <td className="px-4 py-3">{pad(r.rt)}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{r.created_by}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-14 text-center text-slate-500" data-testid="agenda-empty">Belum ada surat keluar pada periode ini.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
